/**
 * PURE FUNCTION - NO REACT IMPORTS
 * 
 * Single entrypoint for all field label resolution in Dynamic Views.
 * Use useResolveFieldLabel() hook in React components.
 */

import type {} from "@/lib/erp/meta";
import type { ResolveFieldLabelParams, ResolveFieldLabelResult } from "./types";
import { getStyleOverride } from "./overrides";
import { getMetaTranslation } from "./translations";
import { humanizeFieldname } from "./utils/humanizeFieldname";

/**
 * Development helper: Get all missing translations that were detected
 * Call this from browser console: window.getMissingTranslations()
 */
if (typeof globalThis !== "undefined" && process.env.NODE_ENV === "development") {
  (globalThis as any).getMissingTranslations = () => {
    const missing = (globalThis as any).__missingTranslations;
    if (!missing || missing.size === 0) {
      console.log("✅ No missing translations detected!");
      return [];
    }
    const entries = Array.from(missing.values());
    console.table(entries);
    return entries;
  };
  
  (globalThis as any).getMissingTranslationsAsCode = () => {
    const missing = (globalThis as any).__missingTranslations;
    if (!missing || missing.size === 0) {
      console.log("✅ No missing translations detected!");
      return "";
    }
    type MissingEntry = { doctype: string; fieldname: string; label: string };
    const entries = Array.from(missing.values()) as MissingEntry[];
    const grouped = entries.reduce((acc, entry) => {
      if (!acc[entry.doctype]) acc[entry.doctype] = [];
      acc[entry.doctype].push(entry);
      return acc;
    }, {} as Record<string, MissingEntry[]>);
    
    let code = "\n// Missing translations - copy this to your translation file:\n\n";
    Object.entries(grouped).forEach(([doctype, fields]) => {
      code += `// ${doctype} fields\n`;
      fields.forEach(entry => {
        code += `"${doctype}:${entry.fieldname}": { en: "${entry.label}", th: "..." },\n`;
      });
      code += "\n";
    });
    
    console.log(code);
    return code;
  };
}

/**
 * Single entrypoint for all field label resolution in Dynamic Views.
 * 
 * PURE FUNCTION - No React dependencies. Use useResolveFieldLabel() hook in components.
 * 
 * Resolution priority (Experience Mode):
 * 1. Style override (doctype + fieldname + style + language)
 * 2. Meta translation dictionary (doctype + fieldname + language)
 * 3. ERP meta field.label
 * 4. Humanized fieldname
 * 
 * Resolution priority (Systems Mode):
 * 1. Meta translation dictionary (only if enableSystemTranslations === true)
 * 2. ERP meta field.label
 * 3. Humanized fieldname
 * 
 * @param params - Label resolution parameters
 * @returns Resolved label with source information
 * 
 * @example
 * ```ts
 * const result = resolveFieldLabel({
 *   doctype: "Item",
 *   field: metaField,
 *   mode: "experience",
 *   style: "shopify",
 *   language: "en",
 * });
 * ```
 */
export function resolveFieldLabel(
  params: ResolveFieldLabelParams
): ResolveFieldLabelResult {
  const {
    doctype,
    field,
    mode,
    style,
    language,
    enableSystemTranslations = false,
  } = params;

  // Runtime guard: style is required in experience mode
  if (mode === "experience" && !style) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `resolveFieldLabel: style is required when mode === "experience" but was not provided for ${doctype}.${field.fieldname}`
      );
    }
    // Fall through to translation/ERP meta (graceful degradation)
  }

  // Priority 1: Experience style override (only in experience mode)
  if (mode === "experience" && style) {
    const override = getStyleOverride(doctype, field.fieldname, style, language);
    if (override) {
      return { label: override, source: "override" };
    }
  }

  // Priority 2: Meta translation dictionary
  // Skip in Systems mode unless explicitly enabled
  if (mode === "experience" || enableSystemTranslations) {
    const translation = getMetaTranslation(doctype, field.fieldname, language);
    if (translation) {
      return { label: translation, source: "translation" };
    }
    
    // Development mode: Track and report missing translations (only for non-English)
    if (process.env.NODE_ENV === "development" && language !== "en" && field.label) {
      // Initialize missing translations tracker if it doesn't exist
      if (!(globalThis as any).__missingTranslations) {
        (globalThis as any).__missingTranslations = new Map<string, {
          doctype: string;
          fieldname: string;
          label: string;
          language: string;
        }>();
        
        // Log summary every 5 seconds
        setInterval(() => {
          const missing = (globalThis as any).__missingTranslations;
          if (missing && missing.size > 0) {
            console.group(`🔍 [Translation Check] Found ${missing.size} missing translation(s):`);
            type MissingEntry = { doctype: string; fieldname: string; label: string };
            const entries = Array.from(missing.values()) as MissingEntry[];
            entries.forEach((entry, idx) => {
              const key = `${entry.doctype}:${entry.fieldname}`;
              console.warn(
                `${idx + 1}. ${entry.doctype}.${entry.fieldname} → "${entry.label}"\n` +
                `   Add: "${key}": { en: "${entry.label}", th: "..." }`
              );
            });
            console.groupEnd();
            
            // Clear after logging to avoid spam
            missing.clear();
          }
        }, 5000);
      }
      
      // Track this missing translation
      const key = `${doctype}_${field.fieldname}_${language}`;
      const tracker = (globalThis as any).__missingTranslations;
      if (!tracker.has(key)) {
        tracker.set(key, {
          doctype,
          fieldname: field.fieldname,
          label: field.label,
          language,
        });
        
        // Also log immediately for first occurrence
        console.warn(
          `⚠️ [Missing Translation] ${doctype}.${field.fieldname} (${language})\n` +
          `   Label: "${field.label}"\n` +
          `   Add: "${doctype}:${field.fieldname}": { en: "${field.label}", th: "..." }`
        );
      }
    }
  }

  // Priority 3: ERP meta field.label
  if (field.label) {
    return { label: field.label, source: "erp_meta" };
  }

  // Priority 4: Humanized fieldname fallback
  const humanized = humanizeFieldname(field.fieldname);
  return { label: humanized, source: "humanized" };
}

